var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { CreatorEventFulfilledDareStatus } from 'root/src/shared/dares/creatorEventFulfilledDare.types';
import { memo } from 'react';
import { useIsAuthenticated } from 'root/src/client/v2/auth/hooks/useIsAuthenticated';
import { useIsAdmin } from 'root/src/client/v2/auth/hooks/useIsAdmin';
import { trpc } from 'root/src/client/v2/trpc/trpc';
import toast from 'react-hot-toast';
import { getErrorMessage } from 'root/src/client/v2/common/hooks/useDareDropApi';
import * as BlackCard from 'root/src/client/v2/common/components/BlackCard/BlackCard';
import { EditCreatorDare } from 'root/src/client/web/record/CreatorData/CreatorDare/EditCreatorDare';
import { ButtonNew, ButtonNewVariant } from 'root/src/client/v2/common/components/form/ButtonNew';
import LoadingCircles from 'root/src/client/v2/common/components/LoadingCircles';
import { ProjectCardLabel, statusColorsMap, statusLabels, statusNamesEnum, } from 'root/src/client/web/list/listItemComponents/project/ProjectCardLabel';
import moment from 'moment-mini';
import { projectAcceptedKey, projectDeliveredKey, projectDeliveryPendingKey, projectPendingKey, projectRejectedKey, } from 'root/src/shared/constants/keys';
import { useCreatorDaresContext } from 'root/src/client/web/record/CreatorData/CreatorDare/creatorDares.context';
import { PlatformIcon, PlatformIconColor, } from 'root/src/client/web/base/CustomButton/PlatformIcon';
import { getReadableSocialPlatform } from 'root/src/shared/platforms/helpers/getReadablePlatform';
import { GameIntegratedCompletionTypeBadge } from 'root/src/client/web/list/listItemComponents/dare/DareCardSeparator';
import { DarePlatformsSection } from 'root/src/client/web/list/listItemComponents/dare/DareCardBody';
import { TRPCClientError } from '@trpc/client';
import { useModal } from 'root/src/client/v2/common/contexts/modal.context';
import { SignInWithProvidersModal } from 'root/src/client/v2/auth/features/SignInWithProvidersModal';
import { SignInWithSocialAccount } from 'root/src/client/web/auth/features/SignInWithSocialAccount';
import { ACCEPT_DARE_ERROR_CODE_MAP } from 'root/src/shared/dares/dares.constants';
export const CreatorDareCard = memo(({ dare, isDareCreator }) => {
    const isAuthenticated = useIsAuthenticated();
    const modal = useModal();
    const { isAdmin } = useIsAdmin();
    const { refetch } = useCreatorDaresContext();
    const acceptDareMutation = trpc.dares.acceptEventFulfilledDare.useMutation();
    const onAccept = () => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b;
        try {
            yield acceptDareMutation.mutateAsync({ dareId: dare.id });
            yield refetch();
            toast.success('Dare accepted');
        }
        catch (err) {
            if (err instanceof TRPCClientError) {
                const errorCode = (_b = (_a = err.data) === null || _a === void 0 ? void 0 : _a.cause) === null || _b === void 0 ? void 0 : _b.errorCode;
                switch (errorCode) {
                    case ACCEPT_DARE_ERROR_CODE_MAP.CREATOR_PLATFORM_NOT_CONNECTED: {
                        modal.show(<SignInWithProvidersModal />);
                        break;
                    }
                    case ACCEPT_DARE_ERROR_CODE_MAP.AUTH_PLATFORM_NOT_CONNECTED: {
                        modal.show(<SignInWithSocialAccount socialPlatform={dare.authPlatform}/>);
                        break;
                    }
                    default:
                        break;
                }
            }
            console.error(err);
            toast.error(getErrorMessage(err));
        }
    });
    const canBeAccepted = !isDareCreator && !dare.project && isAuthenticated;
    const isEditable = (isDareCreator && dare.status === CreatorEventFulfilledDareStatus.Enabled) || isAdmin;
    return (<BlackCard.Root>
			<BlackCard.Header>
				{isEditable && <EditCreatorDare dare={dare}/>}
				<DareCardStatusOverlay status={dare.status}/>
				{canBeAccepted && (<div className="hidden absolute w-full h-full z-[1] left-0 top-0 group-hover:flex bg-[rgba(0,0,0,.7)] duration-150 justify-center items-center">
						<ButtonNew className="uppercase tracking-wide flex flex-initial w-32 justify-center items-center h-12" onClick={onAccept} disabled={acceptDareMutation.isPending} variant={ButtonNewVariant.Secondary}>
							{acceptDareMutation.isPending ? <LoadingCircles width={32} height={32} className="flex flex-initial mr-2"/> : <>Accept</>}
						</ButtonNew>
					</div>)}
				{dare.project && (<div className="absolute top-4 z-[10] left-4">
						{getProjectLabels(dare.project).map(({ label, color }) => (<ProjectCardLabel key={label} status={label} color={color}/>))}
					</div>)}
				<img src={dare.game.image.replace('{width}x{height}', '210x310')} alt={`${dare.game.name} logo`} className="w-full group-hover:scale-110 duration-100 h-full object-cover object-top"/>
			</BlackCard.Header>
			<BlackCard.Separator>
				<div className="flex justify-between px-2">
					<div className="flex flex-initial items-center gap-4 px-2">
						<div className="relative">
							<img className="scale-110 drop-shadow-primary-glow border-2 border-white w-10 h-10 rounded-full flex flex-initial" src={dare.creator.image} alt={dare.creator.displayName}/>
							<div className="bg-white rounded-full flex justify-center items-center drop-shadow-primary-glow h-5 w-5 absolute -bottom-1 -right-2">
								<PlatformIcon platform={dare.creator.platform} color={PlatformIconColor.Dark} size="sm"/>
							</div>
						</div>
						<p className="text-sm">
							{dare.title}
						</p>
					</div>
					<div className="flex flex-initial items-center gap-1">
						<GameIntegratedCompletionTypeBadge />
					</div>
				</div>
			</BlackCard.Separator>
			<BlackCard.Body>
				<div className="py-4 px-1 flex flex-col gap-4">
					<p className="text-sm">
						{dare.description}
					</p>
					<DarePlatformsSection 
    // @ts-expect-error
    platforms={dare.platforms || []}/>
				</div>
				{dare.authPlatform && (<div className="py-2">
						<p className="text-xs text-gray-400 italic">Required {getReadableSocialPlatform(dare.authPlatform)} account.</p>
					</div>)}
			</BlackCard.Body>
		</BlackCard.Root>);
});
const getProjectLabels = (project) => {
    const labels = [];
    const { status } = project;
    const timeLeftMod = moment(project.expirationDate).diff(moment().format(), 'seconds');
    const pendingStatus = status === projectPendingKey;
    const acceptedStatus = status === projectAcceptedKey;
    const deliveryPendingStatus = status === projectDeliveryPendingKey;
    const completedStatus = status === projectDeliveredKey;
    const expiredStatus = !completedStatus && timeLeftMod <= 0;
    const rejectedStatus = status === projectRejectedKey;
    if (pendingStatus) {
        labels.push({ label: statusLabels[statusNamesEnum.pending], color: statusColorsMap[statusNamesEnum.pending] });
    }
    if (acceptedStatus) {
        labels.push({ label: statusLabels[statusNamesEnum.accepted], color: statusColorsMap[statusNamesEnum.accepted] });
    }
    if (deliveryPendingStatus) {
        labels.push({ label: statusLabels[statusNamesEnum.inReview], color: statusColorsMap[statusNamesEnum.inReview] });
    }
    if (completedStatus) {
        labels.push({ label: statusLabels[statusNamesEnum.delivered], color: statusColorsMap[statusNamesEnum.delivered] });
    }
    if (rejectedStatus) {
        labels.push({ label: statusLabels[statusNamesEnum.rejected], color: statusColorsMap[statusNamesEnum.rejected] });
    }
    if (expiredStatus) {
        labels.push({ label: statusLabels[statusNamesEnum.expired], color: statusColorsMap[statusNamesEnum.expired] });
    }
    return labels;
};
const DareCardStatusOverlay = memo(({ status }) => {
    if (status === CreatorEventFulfilledDareStatus.Pending) {
        return (<div className="pointer-events-none absolute w-full h-full z-[10] left-0 top-0 flex bg-[rgba(0,0,0,.7)] justify-center items-center">
				<p className="text-teal border-teal bg-[rgba(0,0,0,.4)] font-bold text-lg font-mono border-2 px-2 py-0.5">Pending Review</p>
			</div>);
    }
    if (status === CreatorEventFulfilledDareStatus.Disabled) {
        return (<div className="pointer-events-none absolute w-full h-full z-[10] left-0 top-0 flex bg-[rgba(0,0,0,.7)] justify-center items-center">
				<p className="font-bold text-lg font-mono border-2 bg-[rgba(0,0,0,.4)] border-pink px-2 py-0.5 text-pink">Disabled</p>
			</div>);
    }
    return null;
});
